import { IconMenu2 } from "@tabler/icons-react"
import {
	createContext,
	type FC,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useId,
	useRef,
	useState,
} from "react"
import { createPortal } from "react-dom"
import { Outlet, useLocation } from "react-router"
import styled from "styled-components"

import { lockBodyScroll } from "@forento/ui/utilities/modalScrollLock"

import ErrorBoundary from "~/components/ErrorBoundary"
import { ChevronIcon, HelpIcon } from "~/components/Icon"
import NewThemeModal from "~/components/Layout/NewThemeModal"
import Link from "~/components/Link"
import { useBrand } from "~/contexts/BrandContext"

import Sidebar from "./Sidebar"
import StatusBanner from "./StatusBanner"
import WorkspaceIdentity from "./WorkspaceIdentity"

const titleFontSize = 24

const asidePortalId = "app-layout-aside"
const AppLayoutContext = createContext<HTMLElement | null | undefined>(undefined)

const AppLayoutFrame: FC<{
	children: ReactNode
	asideContent?: ReactNode
	asidePortalRef?: (element: HTMLDivElement | null) => void
}> = ({ children, asideContent, asidePortalRef }) => {
	const brand = useBrand()
	const { pathname } = useLocation()
	const sidebarId = useId()
	const [isDrawerOpen, setDrawerOpen] = useState(false)
	const menuButtonRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (!isDrawerOpen) return
		return lockBodyScroll()
	}, [isDrawerOpen])

	return (
		<div className="flex min-h-screen bg-white">
			<Sidebar
				id={sidebarId}
				isDrawerOpen={isDrawerOpen}
				setDrawerOpen={setDrawerOpen}
				externalToggleRef={menuButtonRef}
			/>
			{/* While the drawer is open, the page behind it is inert and doesn't scroll. */}
			<div className="flex min-w-0 flex-1 flex-col" inert={isDrawerOpen}>
				{/* On phones this bar holds the only navigation control, so it stays in view. */}
				<div className="border-component-border flex items-center gap-2 border-b bg-white px-2 py-1.5 max-sm:sticky max-sm:top-0 max-sm:z-30 sm:px-6 sm:py-2.5 lg:hidden">
					<button
						ref={menuButtonRef}
						type="button"
						onClick={() => setDrawerOpen(true)}
						aria-expanded={isDrawerOpen}
						aria-controls={sidebarId}
						aria-label="Open navigation"
						className="focus-visible:outline-primary flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-black/4 hover:text-slate-950 focus-visible:outline-2 focus-visible:outline-offset-2 sm:hidden"
					>
						<IconMenu2 className="size-5" />
					</button>
					<WorkspaceIdentity className="min-w-0" />
				</div>
				<StatusBanner />
				<NewThemeModal />
				<div className="flex min-w-0 flex-1">
					<ErrorBoundary key={pathname}>
						<div className="max-w-page-content flex min-w-0 flex-1 flex-col">
							<div className="flex min-w-0 flex-1 flex-col p-4 sm:p-6 xl:p-8">{children}</div>
							<div className="flex flex-col items-center justify-center space-y-2 py-6 text-center">
								<div className="text-sm text-gray-600">
									&copy; {new Date().getFullYear()} {brand.legalName}. All rights reserved.
								</div>
								<div className="flex items-center space-x-6 text-sm">
									<a
										href="https://forento.link/terms"
										target="_blank"
										rel="noreferrer"
										className="text-gray-500 underline-offset-2 transition-colors duration-200 hover:text-gray-700 hover:underline"
									>
										Terms of service
									</a>
									<div className="size-1 rounded-full bg-gray-400" />
									<a
										href="https://forento.link/privacy"
										target="_blank"
										rel="noreferrer"
										className="text-gray-500 underline-offset-2 transition-colors duration-200 hover:text-gray-700 hover:underline"
									>
										Privacy policy
									</a>
								</div>
							</div>
						</div>
					</ErrorBoundary>
					{asidePortalRef !== undefined ? (
						<div ref={asidePortalRef} id={asidePortalId} className="contents" />
					) : (
						asideContent
					)}
				</div>
			</div>
		</div>
	)
}

export const AppLayout: FC = () => {
	const [asidePortal, setAsidePortal] = useState<HTMLDivElement | null>(null)
	const handleAsidePortalRef = useCallback((element: HTMLDivElement | null) => setAsidePortal(element), [])

	return (
		<AppLayoutContext.Provider value={asidePortal}>
			<AppLayoutFrame asidePortalRef={handleAsidePortalRef}>
				<Outlet />
			</AppLayoutFrame>
		</AppLayoutContext.Provider>
	)
}

const Layout: FC<{ asideContent?: ReactNode; children: ReactNode }> = ({ asideContent, children }) => {
	const asidePortal = useContext(AppLayoutContext)

	if (asidePortal === undefined) return <AppLayoutFrame asideContent={asideContent}>{children}</AppLayoutFrame>

	return (
		<>
			{children}
			{asideContent !== undefined && asidePortal !== null && createPortal(asideContent, asidePortal)}
		</>
	)
}

export const PageTitle = styled.h1`
	font-weight: 600;
	font-size: ${titleFontSize}px;
	color: #1a1a1a;
	line-height: 1.25;
	overflow-wrap: anywhere;
	margin-bottom: 22px;
`

const BreadcrumbContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
	margin-bottom: 22px;

	${PageTitle} {
		margin-bottom: 0;
	}
`

export const HelpCenter = styled.a`
	color: #7d7d7d;
	font-size: 16px;
	font-weight: 600;
	line-height: 1;
	display: flex;
	align-items: center;
	gap: 8px;
`

export const HelpCenterIcon = styled(HelpIcon)`
	width: 24px;
	height: 24px;
`

export const PageHeader = styled.header`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	margin-bottom: 22px;

	${PageTitle}, ${BreadcrumbContainer} {
		margin-bottom: 0;
	}
`

export const PageHeaderGroup = styled.div`
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
`

export const PageBreadcrumb: FC<{ title: string; path: { title: string; link?: string }[] }> = ({ title, path }) => (
	<BreadcrumbContainer>
		<nav aria-label="Breadcrumb">
			<ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm leading-5 font-medium text-gray-500">
				{path.map(({ title, link }, index) => (
					<li key={index} className="flex min-w-0 items-center gap-1">
						{index > 0 && <ChevronIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true" />}
						{link !== undefined ? (
							<Link
								to={link}
								className="focus-visible:outline-primary rounded-sm transition-colors duration-150 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2"
							>
								{title}
							</Link>
						) : (
							<span>{title}</span>
						)}
					</li>
				))}
			</ol>
		</nav>
		<PageTitle>{title}</PageTitle>
	</BreadcrumbContainer>
)

export default Layout
